import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION } from '../version';
import { recibosCobroAPI, configuracionAPI } from '../services/api';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { useBranding } from '../context/BrandingContext';
import { SidebarMenuGroup, SideItem, openSectionsForPath } from './SidebarMenuGroup';
import { NavIcon } from './NavIcon';
import '../Layout.css';

const Layout = () => {
  const { user, logout, hasPermission } = useAuth();
  const { logoUrl } = useBranding();
  const { formatFecha, reloj, etiquetaZona } = useZonaHoraria();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarVisible, setSidebarVisible] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
      return false;
    }
    return true;
  });
  const [alertasCheques, setAlertasCheques] = useState(null);
  const [modalAlertasOpen, setModalAlertasOpen] = useState(false);
  const alertasFetchedRef = useRef(false);
  const [avisoHorario, setAvisoHorario] = useState(null);
  const [relojSistema, setRelojSistema] = useState('');
  const [openSections, setOpenSections] = useState(() => openSectionsForPath(
    typeof window !== 'undefined' ? window.location.pathname : ''
  ));

  const toggleSection = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const prev = sessionStorage.getItem('app_last_path') || '';
    sessionStorage.setItem('app_prev_path', prev);
    sessionStorage.setItem('app_last_path', location.pathname);
    setOpenSections((prev) => ({ ...prev, ...openSectionsForPath(location.pathname) }));
    if (window.matchMedia('(max-width: 768px)').matches) {
      setSidebarVisible(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user || !hasPermission('ver_movimientos') || alertasFetchedRef.current) return;
    alertasFetchedRef.current = true;
    recibosCobroAPI.getAlertasCheques()
      .then((res) => {
        if (res.success && res.data) {
          const { carteraProximosVencer = [], emitidosHoyEmitido = [] } = res.data;
          if (carteraProximosVencer.length > 0 || emitidosHoyEmitido.length > 0) {
            setAlertasCheques(res.data);
            setModalAlertasOpen(true);
          }
        }
      })
      .catch(() => {});
  }, [user, hasPermission]);

  useEffect(() => {
    if (!user) return;
    configuracionAPI
      .getSistema()
      .then((res) => {
        if (res.success && res.data?.usar_horarios && res.data.dentro_horario === false) {
          setAvisoHorario(res.data.mensaje_horario || 'Fuera del horario de uso del sistema.');
        } else {
          setAvisoHorario(null);
        }
      })
      .catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user) return;
    const tick = () => setRelojSistema(reloj());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [user, reloj]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="layout">
      {sidebarVisible && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Cerrar menú"
          onClick={() => setSidebarVisible(false)}
        />
      )}
      {!sidebarVisible && (
        <button
          type="button"
          className="sidebar-mobile-open"
          onClick={() => setSidebarVisible(true)}
          aria-label="Abrir menú"
        >
          ☰ Menú
        </button>
      )}
      <nav className={`sidebar ${sidebarVisible ? '' : 'sidebar-collapsed'}`}>
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => setSidebarVisible(!sidebarVisible)}
          title={sidebarVisible ? 'Ocultar menú' : 'Mostrar menú'}
          aria-label={sidebarVisible ? 'Ocultar menú' : 'Mostrar menú'}
        >
          {sidebarVisible ? '◀' : '▶'}
        </button>
        <div className="sidebar-header">
          {logoUrl ? (
            <img className="sidebar-logo" src={logoUrl} alt="Logo de la empresa" />
          ) : null}
          <h2>Sistema de Abonos</h2>
        </div>
        <ul className="sidebar-menu">
          {hasPermission('ver_informes') && (
            <SideItem to="/dashboard" icon="dashboard">Dashboard</SideItem>
          )}
          {hasPermission('ver_clientes') && (
            <SidebarMenuGroup
              id="clientes"
              icon="users"
              label="Clientes"
              isOpen={!!openSections.clientes}
              onToggle={toggleSection}
            >
              <SideItem to="/clientes" end icon="list">Lista</SideItem>
              {hasPermission('ver_informes') && (
                <SideItem to="/clientes/fichas" icon="cards">Fichas</SideItem>
              )}
              {hasPermission('gestionar_usuarios') && (
                <SideItem to="/tipos-clientes" icon="tag">Tipos de Clientes</SideItem>
              )}
              {hasPermission('ver_clientes') && (
                <SideItem to="/grupos" icon="groups">Grupos</SideItem>
              )}
              {(hasPermission('ver_movimientos') || hasPermission('ver_movimientos_clientes')) && (
                <SideItem to="/movimientos" icon="receipt">Movimientos clientes</SideItem>
              )}
              {hasPermission('ver_facturacion') && (
                <SideItem to="/clientes/facturacion" icon="receipt">Facturación</SideItem>
              )}
            </SidebarMenuGroup>
          )}
          {hasPermission('ver_clientes') && (
            <SidebarMenuGroup
              id="proveedores"
              icon="truck"
              label="Proveedores"
              isOpen={!!openSections.proveedores}
              onToggle={toggleSection}
            >
              <SideItem to="/proveedores" end icon="list">Lista</SideItem>
              {hasPermission('ver_informes') && (
                <SideItem to="/proveedores/fichas" icon="cards">Fichas</SideItem>
              )}
              {hasPermission('gestionar_usuarios') && (
                <SideItem to="/tipos-proveedores" icon="tag">Tipos de Proveedores</SideItem>
              )}
              {(hasPermission('ver_movimientos') || hasPermission('ver_movimientos_proveedores')) && (
                <SideItem to="/movimientos-proveedores" icon="receipt">Movimientos proveedores</SideItem>
              )}
              {hasPermission('ver_gastos') && (
                <SideItem to="/gastos" icon="wallet">Gastos</SideItem>
              )}
            </SidebarMenuGroup>
          )}
          {(hasPermission('ver_movimientos') || hasPermission('ver_fondos_listado') || hasPermission('gestionar_usuarios') || hasPermission('ver_bancos') || hasPermission('ver_cuentas_bancarias')) && (
            <SidebarMenuGroup
              id="fondos"
              icon="wallet"
              label="Fondos"
              isOpen={!!openSections.fondos}
              onToggle={toggleSection}
            >
              {hasPermission('ver_fondos_listado') && (
                <SideItem to="/fondos" icon="list">Lista de fondos</SideItem>
              )}
              {hasPermission('cerrar_caja') && (
                <SideItem to="/cierre-caja" icon="lock">Cierre de caja</SideItem>
              )}
              {(hasPermission('cerrar_caja') || hasPermission('ver_fondos_listado')) && (
                <SideItem to="/cierres-caja" icon="history">Historial cierres</SideItem>
              )}
              {hasPermission('ver_informes') && (
                <SideItem to="/proyeccion-fondos" icon="chart">Proyección fondos</SideItem>
              )}
              {hasPermission('conciliar_cuentas_bancarias') && (
                <SideItem to="/conciliacion-bancaria" icon="bank">Conciliación bancaria</SideItem>
              )}
              {hasPermission('ver_informes') && (
                <SideItem to="/fondos/fichas" icon="cards">Fichas</SideItem>
              )}
              {hasPermission('ver_movimientos') && (
                <SideItem to="/movimientos-fondos" icon="receipt">Movimientos fondos</SideItem>
              )}
              {hasPermission('ver_movimientos') && (
                <SideItem to="/cartera-cheques" icon="cheque">Cartera de cheques</SideItem>
              )}
              {hasPermission('ver_movimientos') && (
                <SideItem to="/cheques-emitidos" icon="cheque">Cheques emitidos</SideItem>
              )}
              {hasPermission('ver_movimientos') && (
                <SideItem to="/formas-pago" icon="card">Formas de Pago</SideItem>
              )}
              {hasPermission('ver_movimientos') && (
                <SideItem to="/cajas" icon="box">Cajas</SideItem>
              )}
              {hasPermission('ver_bancos') && (
                <SideItem to="/bancos" icon="bank">Bancos</SideItem>
              )}
              {hasPermission('ver_cuentas_bancarias') && (
                <SideItem to="/cuentas-bancarias" icon="bank">Cuentas Bancarias</SideItem>
              )}
            </SidebarMenuGroup>
          )}
          {hasPermission('ver_clientes') && (
            <SidebarMenuGroup
              id="empleados"
              icon="user"
              label="Empleados"
              isOpen={!!openSections.empleados}
              onToggle={toggleSection}
            >
              <SideItem to="/empleados" end icon="list">Lista</SideItem>
              {hasPermission('ver_informes') && (
                <SideItem to="/empleados/fichas" icon="cards">Fichas</SideItem>
              )}
              <SideItem to="/tipos-movimiento-persona" icon="receipt">Movimientos de personas</SideItem>
            </SidebarMenuGroup>
          )}
          {hasPermission('ver_movimientos') && (
            <SideItem to="/movimientos-predefinidos" icon="calendar">Movimientos predefinidos</SideItem>
          )}
          {hasPermission('ver_movimientos_programados') && (
            <SideItem to="/movimientos-programados" icon="calendar">Movimientos programados</SideItem>
          )}
          {(hasPermission('ver_informes') || hasPermission('ver_clientes')) && (
            <SidebarMenuGroup
              id="informes"
              icon="chart"
              label="Informes"
              isOpen={!!openSections.informes}
              onToggle={toggleSection}
            >
              {hasPermission('ver_informes') && (
                <>
                  <SideItem to="/informes" icon="chart">Informes</SideItem>
                  <SideItem to="/proyeccion-fondos" icon="chart">Proyección fondos</SideItem>
                  {hasPermission('ver_resultados_reales') && (
                    <SideItem to="/resultados-reales" icon="chart">Resultados reales</SideItem>
                  )}
                  <SideItem to="/dashboard-fichas" icon="cards">Fichas del dashboard</SideItem>
                </>
              )}
              {hasPermission('ver_clientes') && (
                <SideItem to="/whatsapp" icon="message">Mensajes</SideItem>
              )}
            </SidebarMenuGroup>
          )}
          <SidebarMenuGroup
            id="configuracion"
            icon="settings"
            label="Configuración"
            isOpen={!!openSections.configuracion}
            onToggle={toggleSection}
          >
            <SideItem to="/configuracion" end icon="settings">General</SideItem>
            {hasPermission('admin_puesta_en_marcha') && (
              <SideItem to="/configuracion/puesta-en-marcha" icon="settings">Puesta en marcha</SideItem>
            )}
            {hasPermission('gestionar_usuarios') && (
              <SideItem to="/configuracion/arca" icon="settings">Conexión ARCA</SideItem>
            )}
          </SidebarMenuGroup>
          {(hasPermission('gestionar_usuarios') || hasPermission('ver_auditoria') || hasPermission('ver_backup')) && (
            <SidebarMenuGroup
              id="seguridad"
              icon="shield"
              label="Seguridad"
              isOpen={!!openSections.seguridad}
              onToggle={toggleSection}
            >
              {hasPermission('gestionar_usuarios') && (
                <SideItem to="/usuarios" icon="user">Usuarios</SideItem>
              )}
              {hasPermission('ver_auditoria') && (
                <SideItem to="/auditoria" icon="audit">Auditoría</SideItem>
              )}
              {hasPermission('ver_backup') && (
                <SideItem to="/backups" icon="backup">Backups</SideItem>
              )}
            </SidebarMenuGroup>
          )}
        </ul>
        <div className="sidebar-footer">
          {relojSistema && (
            <div
              className="sidebar-reloj-sistema"
              style={{
                fontSize: '0.78rem',
                color: '#bdc3c7',
                lineHeight: 1.35,
                marginBottom: '10px',
                textTransform: 'capitalize',
              }}
              title={`Hora del sistema (${etiquetaZona()})`}
            >
              {relojSistema}
            </div>
          )}
          <div className="user-info">
            <p>{user?.nombre}</p>
            <p className="app-version">v{APP_VERSION}</p>
            <button onClick={handleLogout} className="logout-btn" title="Cerrar sesión" data-label="Cerrar sesión">
              <NavIcon name="logout" />
              <span className="nav-label">Cerrar sesión</span>
            </button>
          </div>
        </div>
      </nav>
      <main className="main-content">
        {avisoHorario && (
          <div
            className="horario-uso-banner"
            style={{
              background: '#fff3cd',
              borderBottom: '1px solid #ffc107',
              padding: '10px 16px',
              fontSize: '0.95rem',
            }}
          >
            <strong>Horario de uso:</strong> {avisoHorario}{' '}
            {hasPermission('gestionar_usuarios') && (
              <Link to="/configuracion" style={{ marginLeft: 8 }}>
                Ver configuración
              </Link>
            )}
          </div>
        )}
        <Outlet />
      </main>

      {modalAlertasOpen && alertasCheques && (
        <div
          className="modal-overlay alertas-cheques-overlay"
          onClick={() => setModalAlertasOpen(false)}
          role="dialog"
          aria-labelledby="alertas-cheques-title"
          aria-modal="true"
        >
          <div className="modal-content alertas-cheques-modal" onClick={(e) => e.stopPropagation()}>
            <div className="alertas-cheques-header">
              <h2 id="alertas-cheques-title">Avisos de cheques</h2>
              <button type="button" className="alertas-cheques-cerrar" onClick={() => setModalAlertasOpen(false)} aria-label="Cerrar">×</button>
            </div>
            <div className="alertas-cheques-body">
              {alertasCheques.carteraProximosVencer && alertasCheques.carteraProximosVencer.length > 0 && (
                <section className="alertas-cheques-seccion">
                  <p className="alertas-cheques-aviso">
                    <strong>Cheques de terceros (cartera) próximos a vencer</strong> — Estado &quot;Recibido&quot; y fecha de cobro entre hoy y los últimos 30 días (hoy hacia atrás). Conviene depositarlos o gestionarlos.
                  </p>
                  <div className="alertas-cheques-tabla-wrapper">
                    <table className="alertas-cheques-tabla">
                      <thead>
                        <tr>
                          <th>Nº cheque</th>
                          <th>Cliente</th>
                          <th>Recibo</th>
                          <th>F. cobro</th>
                          <th>Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alertasCheques.carteraProximosVencer.map((ch) => (
                          <tr key={`cartera-${ch.id}`}>
                            <td>{ch.numero}</td>
                            <td>{ch.cliente_razon_social || '-'}</td>
                            <td>{ch.numero_recibo || '-'}</td>
                            <td>{formatFecha(ch.fecha_cobro)}</td>
                            <td>{typeof ch.importe === 'number' ? ch.importe.toLocaleString('es-AR') : ch.importe}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
              {alertasCheques.emitidosHoyEmitido && alertasCheques.emitidosHoyEmitido.length > 0 && (
                <section className="alertas-cheques-seccion">
                  <p className="alertas-cheques-aviso">
                    <strong>Cheques emitidos con fecha de cobro hoy y aún en estado &quot;Emitido&quot;</strong> — Podrían ser depositados por la persona a quien se los entregamos.
                  </p>
                  <div className="alertas-cheques-tabla-wrapper">
                    <table className="alertas-cheques-tabla">
                      <thead>
                        <tr>
                          <th>Nº cheque</th>
                          <th>Cliente / Comprobante</th>
                          <th>F. cobro</th>
                          <th>Importe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alertasCheques.emitidosHoyEmitido.map((ch) => (
                          <tr key={`emitido-${ch.id}`}>
                            <td>{ch.numero}</td>
                            <td>{ch.cliente_razon_social || ch.numero_recibo || '-'}</td>
                            <td>{formatFecha(ch.fecha_cobro)}</td>
                            <td>{typeof ch.importe === 'number' ? ch.importe.toLocaleString('es-AR') : ch.importe}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
            <div className="alertas-cheques-footer">
              <button type="button" className="btn-primary" onClick={() => setModalAlertasOpen(false)}>Entendido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;

